import React, { useState, useEffect, createContext, useContext } from 'react';
import { Cookie } from 'lucide-react';

const CONSENT_KEY = 'cookie_consent';

// Context for cookie consent state
export const CookieConsentContext = createContext({
  consentGiven: false,
  consentDeclined: false,
});

export const useCookieConsent = () => useContext(CookieConsentContext);

// Helper function to check consent status
export const getConsentStatus = () => {
  const consent = localStorage.getItem(CONSENT_KEY);
  return {
    consentGiven: consent === 'accepted',
    consentDeclined: consent === 'declined',
    noDecision: !consent,
  };
};

// Facebook Pixel ID
const FB_PIXEL_ID = '2397874900551700';

// Helper to track Facebook Pixel events
export const trackFBEvent = (eventName, params = {}) => {
  if (window.fbq && getConsentStatus().consentGiven) {
    window.fbq('track', eventName, params);
    console.log(`[Tracking] FB Event: ${eventName}`, params);
  }
};

// Helper to track Facebook Pixel CUSTOM events (non-standard event names)
export const trackFBCustom = (eventName, params = {}) => {
  if (window.fbq && getConsentStatus().consentGiven) {
    window.fbq('trackCustom', eventName, params);
    console.log(`[Tracking] FB Custom Event: ${eventName}`, params);
  }
};

// Helper function to load tracking scripts only if consent was given
export const loadTrackingScripts = () => {
  const { consentGiven } = getConsentStatus();
  
  if (!consentGiven) {
    console.log('[Tracking] User has not given consent, skipping tracking scripts');
    return false;
  }

  // Google Analytics placeholder
  // Uncomment and add your GA ID when ready
  /*
  if (!window.gtag) {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=YOUR_GA_ID';
    document.head.appendChild(script);
    
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'YOUR_GA_ID');
  }
  */

  // Facebook Pixel
  if (!window.fbq) {
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', FB_PIXEL_ID);
    window.fbq('track', 'PageView');
    console.log('[Tracking] Facebook Pixel loaded');
  }

  console.log('[Tracking] Consent given - tracking scripts loaded');
  return true;
};

// Helper function to remove/disable tracking
export const disableTracking = () => {
  // Disable Google Analytics
  window['ga-disable-YOUR_GA_ID'] = true;
  
  // Clear any existing tracking cookies
  document.cookie.split(';').forEach((c) => {
    const cookie = c.trim();
    if (cookie.startsWith('_ga') || cookie.startsWith('_fb')) {
      document.cookie = cookie.split('=')[0] + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    }
  });

  console.log('[Tracking] Tracking disabled and cookies cleared');
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user already gave consent
    const { consentGiven, consentDeclined, noDecision } = getConsentStatus();
    
    if (noDecision) {
      // Show popup after a small delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    
    // If consent was given previously, load tracking scripts
    if (consentGiven) {
      loadTrackingScripts();
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setIsVisible(false);
    // Enable tracking (analytics, pixels, etc.)
    loadTrackingScripts();
    window.dispatchEvent(new CustomEvent('cookieConsentAccepted'));
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    setIsVisible(false);
    // Disable tracking and clear cookies
    disableTracking();
    window.dispatchEvent(new CustomEvent('cookieConsentDeclined'));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] flex items-end justify-center px-3 pb-3 sm:px-4 sm:pb-4 pointer-events-none">
      <div
        className="relative pointer-events-auto w-full max-w-xl bg-white rounded-2xl border border-[#C5366A]/15 shadow-[0_25px_80px_-20px_rgba(197,54,106,0.35)] overflow-hidden animate-cookie-slide-up"
        dir="rtl"
        role="dialog"
        aria-labelledby="cookie-title"
        aria-describedby="cookie-description"
        data-testid="cookie-consent-popup"
      >
        {/* Top brand accent bar */}
        <div className="h-1 bg-gradient-to-l from-[#C5366A] via-[#e84393] to-[#C5366A]" aria-hidden />

        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#C5366A]/[0.08] border border-[#C5366A]/20 flex items-center justify-center">
              <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-[#C5366A]" strokeWidth={2.2} />
            </div>

            {/* Body */}
            <div className="flex-1 min-w-0">
              <h3 id="cookie-title" className="text-[#2D2D2D] text-base sm:text-lg font-black mb-1.5 leading-tight">
                האתר משתמש בעוגיות (Cookies)
              </h3>
              <p id="cookie-description" className="text-[#4A4A4A] text-[13px] sm:text-sm leading-relaxed">
                אנו משתמשים בעוגיות ואמצעי מעקב לטובת מיטוב הגלישה וחוויית המשתמש. המידע מסייע לנו להתאים את התכנים והשירותים לצרכיך.
                למידע נוסף, ראה את{' '}
                <a href="/privacy" className="text-[#C5366A] font-semibold hover:underline underline-offset-2">
                  מדיניות הפרטיות
                </a>{' '}
                שלנו.
              </p>
            </div>
          </div>

          {/* Buttons: in RTL the first DOM child appears on the right (primary first). */}
          <div className="grid grid-cols-2 gap-2.5 sm:gap-3 mt-5">
            <button
              type="button"
              onClick={handleAccept}
              className="px-4 py-3 sm:py-3.5 rounded-xl bg-gradient-to-l from-[#C5366A] to-[#e84393] hover:from-[#b32d5d] hover:to-[#d63281] text-white text-sm font-black shadow-[0_8px_20px_-6px_rgba(197,54,106,0.5)] hover:shadow-[0_12px_28px_-6px_rgba(197,54,106,0.65)] hover:-translate-y-0.5 active:translate-y-0 transition-all"
              data-testid="cookie-accept-btn"
            >
              אני מאשר/ת
            </button>
            <button
              type="button"
              onClick={handleDecline}
              className="px-4 py-3 sm:py-3.5 rounded-xl bg-white border border-[#E5E5E5] text-[#4A4A4A] text-sm font-bold hover:bg-[#FAFAFA] hover:border-[#C5366A]/30 hover:text-[#2D2D2D] active:scale-[0.98] transition-all"
              data-testid="cookie-decline-btn"
            >
              אני מסרב/ת
            </button>
          </div>
        </div>
      </div>

      {/* Animation (scoped) */}
      <style>{`
        @keyframes cookieSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-cookie-slide-up { animation: cookieSlideUp 380ms cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </div>
  );
};

export default CookieConsent;
